import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import { Divider, Stack } from '@mui/material';
import { AggregatedSupplierInfo } from './AggregatedSupplierInfo';
import {
  PriceControlFilters,
  PriceControlSupplierItemsTable,
} from '@retail/price-control/components';
import {
  useFetchPriceControlFilterContext,
  useFetchPriceControlSupplierAggregated,
  usePriceControlItemEventAction,
} from '@retail/price-control/data-access';
import {
  PriceControlAction,
  PriceControlEventAction,
  PriceControlItem,
  PriceControlChangeType,
  PriceControlSupplierAggregated,
} from '@retail/price-control/types';
import { useCallback, useMemo, useState } from 'react';
import { HandleItemContainer } from './HandleItemContainer';
import { TotalItemsLoader } from '@retail/products/components';
import {
  ErrorFallback,
  FilterChipContainer,
  SuspenseWithSentryErrorBoundary,
  ToggleFiltersButton,
  ToggleFiltersToolbar,
} from '@retail/components';
import { useToast } from '@retail/hooks';
import { useDisclosure } from '@shared/hooks';
import { useFilters } from '@retail/products/hooks';
import { useAppTFunction } from '@retail/app/i18n';
import { priceControlFilters, PriceControlFilterOptions } from '@retail/my-assortment/utils';
import { useQueryClient } from '@tanstack/react-query';
import { OutlinedButton } from '@shared/components';
import { HandleItemsEventActionDialog } from '.';
import { getISO_8601Date } from '@retail/utils';
import { HierarchyFilterContext } from '@retail/my-assortment/types';

interface Props {
  supplierNumber: number;
}

// The range slider uses -51/51 as its min/max thumbs to represent "no bound".
// Only send an actual bound when the thumb has been moved off its end sentinel.
const RANGE_MIN = -51;
const RANGE_MAX = 51;

const toLowerBound = (value?: string) => {
  const parsed = Number(value);
  return value != null && value !== '' && parsed > RANGE_MIN ? parsed : undefined;
};

const toUpperBound = (value?: string) => {
  const parsed = Number(value);
  return value != null && value !== '' && parsed < RANGE_MAX ? parsed : undefined;
};

export function PriceControlSupplierContainer({ supplierNumber }: Props) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { assortmentId, appTenant } = useSelectedOrgUnit();
  const t = useAppTFunction();

  const { isOpen: includeVat, onToggle: toggleIncludeVat } = useDisclosure(true);
  const { isOpen: showFilters, onToggle: toggleFilters } = useDisclosure();

  const { filters } = useFilters<PriceControlFilterOptions>(priceControlFilters);
  queryClient.invalidateQueries({ queryKey: ['orgUnit-assortments'] });

  const [itemToHandle, setItemToHandle] = useState<PriceControlItem | undefined>(undefined);
  const [checkedItems, setCheckedItems] = useState<number[]>([]);
  const {
    isOpen: isItemsEventActionOpen,
    onOpen: openItemsEventAction,
    onClose: closeItemsEventAction,
  } = useDisclosure();

  const { data: priceControlSupplierDTO, isLoading } = useFetchPriceControlSupplierAggregated({
    assortmentId: assortmentId,
    supplierNumber,
    filters: {
      changeTriggerType:
        filters.changeTrigger === 'ALL'
          ? undefined
          : (filters.changeTrigger as PriceControlChangeType),
      itemSearch: filters.searchQuery ?? undefined,
      productGroupId: filters.productGroupNumber ?? undefined,
      discountGroupId: filters.discountGroupCode ?? undefined,
      changeDateGreaterThanOrEqual: filters.validFrom
        ? getISO_8601Date(new Date(filters.validFrom))
        : undefined,
      purchasePriceChangeLower: toLowerBound(filters.purchasePriceChangeRange[0]),
      purchasePriceChangeUpper: toUpperBound(filters.purchasePriceChangeRange[1]),
      salesPriceChangeLower: toLowerBound(filters.salesPriceChangeRange[0]),
      salesPriceChangeUpper: toUpperBound(filters.salesPriceChangeRange[1]),
      coverageDegreeChangeLower: toLowerBound(filters.covergeDegreeChangeRange[0]),
      coverageDegreeChangeUpper: toUpperBound(filters.covergeDegreeChangeRange[1]),
      isHandledStatus: filters.isUnhandled,
    },
    suspense: false,
  });

  const priceControlSupplier = priceControlSupplierDTO
    ? PriceControlSupplierAggregated.fromDto(priceControlSupplierDTO)
    : undefined;

  const { mutateAsync: postItemEventAction } = usePriceControlItemEventAction({
    assortmentId,
  });
  const handleItemEventAction = useCallback(
    (action: PriceControlAction) => {
      const itemEvent: PriceControlEventAction[] = checkedItems.map((itemId) => {
        return { priceControlMemberEventId: itemId, action };
      });
      postItemEventAction({
        body: itemEvent,
      })
        .then(() => {
          toast.success(t('myAssortment.priceControl.itemHandled'));
          queryClient.invalidateQueries({ queryKey: ['price-control-supplier-aggregated'] });
          setCheckedItems([]);
        })
        .catch((e) => {
          console.error(e);
          toast.error(t('myAssortment.actions.genericActionError'));
        });
    },
    [checkedItems, postItemEventAction, queryClient, t, toast]
  );

  const { data: filterContextDTO, isLoading: isLoadingFilterContext } =
    useFetchPriceControlFilterContext({ assortmentId, supplierNumber });
  const filterContext = useMemo(
    () => (filterContextDTO ? HierarchyFilterContext.fromDto(filterContextDTO) : undefined),
    [filterContextDTO]
  );

  return (
    <Stack flexGrow={1} overflow="hidden">
      <AggregatedSupplierInfo
        priceControlSupplier={priceControlSupplier}
        isLoadingFetchItems={isLoading}
      />
      <ToggleFiltersToolbar sx={{ mb: 1, justifyContent: 'space-between', alignItems: 'center' }}>
        <FilterChipContainer allowedKeys={priceControlFilters} filterOptions={filterContext} />
        <Stack flexDirection="row" alignItems="center" gap={1}>
          <TotalItemsLoader
            totalItems={priceControlSupplier?.items.length ?? 0}
            itemsShowing={priceControlSupplier?.items.length}
            isFetching={isLoading}
          />
          <OutlinedButton
            size="small"
            disabled={!checkedItems.length}
            onClick={openItemsEventAction}
          >
            {t('myAssortment.actions.handleItems')}
          </OutlinedButton>

          <ToggleFiltersButton
            showFilters={showFilters}
            toggleFilters={toggleFilters}
            buttonHeight={50}
            sx={{ mb: -1.24 }}
          />
        </Stack>
      </ToggleFiltersToolbar>
      <Divider />
      <Stack flexGrow={1} flexDirection="row" overflow="hidden">
        <PriceControlSupplierItemsTable
          supplierItems={priceControlSupplier?.items ?? []}
          appTenant={appTenant}
          isLoadingFetchItems={isLoading}
          selectedItemId={itemToHandle?.mgItemNumber}
          checkedItems={checkedItems}
          includeVat={includeVat}
          setItemToHandle={setItemToHandle}
          setCheckedItems={setCheckedItems}
        />

        {itemToHandle && (
          <SuspenseWithSentryErrorBoundary errorFallback={<ErrorFallback />}>
            <HandleItemContainer
              assortmentId={assortmentId}
              priceControlItem={itemToHandle}
              includeVat={includeVat}
              onClose={() => setItemToHandle(undefined)}
            />
          </SuspenseWithSentryErrorBoundary>
        )}
        <PriceControlFilters
          showFilters={showFilters}
          includeVat={includeVat}
          filterContext={filterContext}
          isLoading={isLoadingFilterContext}
          toggleVat={toggleIncludeVat}
        />
      </Stack>

      {isItemsEventActionOpen && (
        <HandleItemsEventActionDialog
          open
          itemIdsToHandle={checkedItems}
          includeVat={includeVat}
          handleItemEventAction={handleItemEventAction}
          onClose={closeItemsEventAction}
        />
      )}
    </Stack>
  );
}
